import type { Animation } from "@pdfking/book3d"
import type { VirtualFile } from "@pdfking/pdfengine"
import type { FileName } from "@pdfking/structs"
import type { ContentRenderer } from "@pdfking/templates"
import type { StringGUID } from "@pdfking/tfunc"
import { Void } from "@pdfking/tfunc"
import { createContext } from "preact"

import type { PdfviewSidebarView } from "#public"

// How the flat 2D plot pairs pages: "single" one per row, "cover" keeps the
// first page alone then pairs the rest (right-hand cover), "double" pairs from
// the first page. 2D-only — the 3D book ignores it.
export type PageGrouping = "single" | "cover" | "double"

/**
 * The full URL-persisted state of a {@link CreatePdfViewer} component. Every
 * control change emits a patch of it through the router, and the host merges
 * that into its current route, so the route carries all the information
 * needed to restore the view.
 */
export type PdfviewState = {
  // The 3D book's camera behavior — "book" (page-turn spread) or "stripe"
  // (flat top-down sheets). Only meaningful while `view_3d`, but routed like
  // it so a shared link/reload restores the chosen mode.
  animation: Animation
  // Whether to render the bleed strip around each page.
  bleed: boolean
  // What the generated content draws every page as, over its grow's own
  // renderer; undefined = each grow's.
  content: ContentRenderer | undefined
  current_page: number
  // The open sidebar panel; undefined = collapsed to the icon rail.
  sidebar: PdfviewSidebarView | undefined
  // Every file referenced by `imposition_data`'s file rows, keyed by name.
  // `VirtualFile` (a `GeneratedFile`/`RemoteFile`, ...) is itself plain JSON
  // — the actual bytes live in the engine's own persistent cache, keyed by
  // this object's `guid`/`mirrors` — so round-tripping it through the URL is
  // enough to restore a file after a real page reload, with no separate
  // session-only cache required. A bare `string` is shorthand for a remote
  // mirror — `CreatePdfViewer` normalizes it into a `RemoteFile` itself, so
  // callers building a deep link (or a seed state) never have to.
  files: Record<FileName, VirtualFile | string>
  hidden_guides: string[]
  imposition_data: Record<string, unknown>
  // The flat 2D plot's page-pairing mode (see {@link PageGrouping}).
  page_grouping: PageGrouping
  // Bumped only when the viewer pushes a new `imposition_data` (a document
  // edit); view-state pushes (page, zoom, sidebar, toggles) leave it as-is.
  // The viewer stamps it so it can recognize the echo of its own document
  // push — and skip every high-frequency view-state echo — with a plain
  // `===`, instead of re-encoding the whole input on each one. A normal
  // route field, so it round-trips like everything else; hosts thread nothing
  // extra.
  revision: StringGUID | undefined
  // Whether the 3D book preview is showing instead of the flat plot.
  view_3d: boolean
  zoom: number
}

/**
 * Navigation adapter the host app provides through {@link PdfviewRouter}: the
 * viewer calls `navigate` with just the fields a control change touched, and
 * the host merges the patch into its current route state (any route name, any
 * router implementation) — one history entry per action, so back = undo. The
 * host's own route is the authoritative full state; the viewer never
 * reassembles (and possibly stalely re-emits) fields it didn't change. The
 * viewer recognizes the echo of its own push by the `revision` it stamped
 * into the pushed state, so no marker is threaded through these calls.
 */
export type PdfviewRouterType = {
  navigate: (patch: Partial<PdfviewState>) => void
  // The url of a *second* viewer showing this state alone — what the file
  // rows' open button opens, with one file in it. Not a patch onto anything:
  // there is no current entry to merge onto in a fresh tab, so the host
  // routes exactly this. A url rather than a "go there" call because where it
  // opens is the build's business (see ExternalLinkType) and what the route is
  // called is the host's; the viewer only knows what it wants shown.
  // `undefined` = this host has nowhere to open a second viewer, so the
  // control does nothing rather than opening a blank tab.
  blank_href: (state: Partial<PdfviewState>) => string | undefined
  // Same merge as `navigate` but replacing the current history entry — used
  // for view state (scrolled page, zoom, sidebar, guide/bleed/3D toggles) so
  // back only steps through document-changing actions.
  replace: (patch: Partial<PdfviewState>) => void
}

// Default is a no-op so the viewer still works (without persistence) when
// embedded outside any provider.
export const PdfviewRouter = createContext<PdfviewRouterType>({
  // Not `Void`: a host that routes nowhere has no url to give, and the control
  // renders unlinked rather than linking somewhere useless.
  blank_href: () => undefined,
  navigate: Void,
  replace: Void,
})
